import React, { useState, useRef } from 'react';
import Icon from './Icon';

/**
 * ReceiptUpload - Mobile-friendly receipt upload component with camera support
 * 
 * Features:
 * - Camera capture button (opens camera on mobile)
 * - Gallery upload option
 * - Image preview (single or multiple)
 * - AI extraction button (Professional+ tier)
 * - Drag and drop support
 * - Multi-image support for long receipts
 */
function ReceiptUpload({ 
  onFileSelect, 
  onFilesSelect,  // New: callback for multiple files
  onAIExtract, 
  file = null,
  files = [],     // New: array of files for multi-image support
  preview = null,
  previews = [],  // New: array of previews for multi-image
  extracting = false,
  extractedData = null,
  showAIButton = false,
  darkMode = true,
  disabled = false,
  label = "Receipt / Invoice",
  accept = "image/*,.pdf",
  multiple = false,  // New: enable multi-file selection
  maxFiles = 5       // New: max number of files for multi-upload
}) {
  const [dragActive, setDragActive] = useState(false);
  const [localPreview, setLocalPreview] = useState(preview);
  const [localPreviews, setLocalPreviews] = useState(previews);
  const [localFiles, setLocalFiles] = useState(files);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Check if a file is an image (handles iOS edge cases where type may be empty)
  const isImageFile = (file) => {
    if (file.type && file.type.startsWith('image/')) return true;
    // Fallback: check file extension for iOS cases where MIME type is empty
    const ext = (file.name || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tiff'].includes(ext);
  };

  // Normalize and optionally compress an image file before upload
  // CRITICAL: Always processes camera photos through canvas to fix EXIF orientation
  // Camera apps store photos rotated with an EXIF tag - canvas drawing fixes this
  const compressImage = (file) => {
    return new Promise((resolve) => {
      // Skip non-image files entirely
      if (!isImageFile(file)) {
        resolve(file);
        return;
      }

      // Skip HEIC/HEIF - browser canvas can't handle them directly, backend will convert
      const ext = (file.name || '').split('.').pop().toLowerCase();
      if (['heic', 'heif'].includes(ext)) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const MAX_SIDE = 2560; // Good quality for AI extraction
            let { width, height } = img;
            
            // Check if resize is needed
            const needsResize = width > MAX_SIDE || height > MAX_SIDE;
            if (needsResize) {
              if (width >= height) {
                height = Math.round(height * MAX_SIDE / width);
                width = MAX_SIDE;
              } else {
                width = Math.round(width * MAX_SIDE / height);
                height = MAX_SIDE;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // For PNG screenshots, preserve PNG format (lossless, better for text)
            const isPng = file.type === 'image/png' || ext === 'png';
            const outputType = isPng ? 'image/png' : 'image/jpeg';
            const outputQuality = isPng ? undefined : 0.92; // Higher JPEG quality for receipts
            
            canvas.toBlob((blob) => {
              if (blob) {
                // Always use the canvas output - it fixes EXIF orientation
                // even if the blob is slightly larger than original
                const normalizedFile = new File([blob], file.name, {
                  type: outputType,
                  lastModified: file.lastModified
                });
                resolve(normalizedFile);
              } else {
                resolve(file); // Canvas blob creation failed, use original
              }
            }, outputType, outputQuality);
          } catch {
            resolve(file); // Canvas processing failed, use original
          }
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    if (multiple && selectedFiles.length > 0) {
      // Compress files in parallel
      const compressed = await Promise.all(selectedFiles.map(f => compressImage(f)));
      processFiles(compressed);
    } else if (selectedFiles[0]) {
      const compressed = await compressImage(selectedFiles[0]);
      processFile(compressed);
    }
  };

  const processFile = (selectedFile) => {
    // Create preview for images (handle iOS empty file.type)
    if (isImageFile(selectedFile)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocalPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setLocalPreview(null);
    }
    
    if (onFileSelect) {
      onFileSelect(selectedFile);
    }
  };

  const processFiles = (newFiles) => {
    // Limit to maxFiles
    const filesToAdd = newFiles.slice(0, maxFiles - localFiles.length);
    if (filesToAdd.length === 0) return;

    const updatedFiles = [...localFiles, ...filesToAdd].slice(0, maxFiles);
    setLocalFiles(updatedFiles);

    // Generate previews for images (handle iOS empty file.type)
    const newPreviews = [...localPreviews];
    filesToAdd.forEach((file, index) => {
      if (isImageFile(file)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLocalPreviews(prev => {
            const updated = [...prev];
            updated[localFiles.length + index] = reader.result;
            return updated;
          });
        };
        reader.readAsDataURL(file);
      }
    });

    if (onFilesSelect) {
      onFilesSelect(updatedFiles);
    }
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = localFiles.filter((_, i) => i !== indexToRemove);
    const updatedPreviews = localPreviews.filter((_, i) => i !== indexToRemove);
    setLocalFiles(updatedFiles);
    setLocalPreviews(updatedPreviews);
    
    if (onFilesSelect) {
      onFilesSelect(updatedFiles);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (multiple && droppedFiles.length > 0) {
      processFiles(droppedFiles);
    } else if (droppedFiles[0]) {
      processFile(droppedFiles[0]);
    }
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleGalleryClick = () => {
    galleryInputRef.current?.click();
  };

  const handleClear = () => {
    setLocalPreview(null);
    setLocalPreviews([]);
    setLocalFiles([]);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (onFileSelect) {
      onFileSelect(null);
    }
    if (onFilesSelect) {
      onFilesSelect([]);
    }
  };

  const currentPreview = preview || localPreview;
  const currentFiles = files.length > 0 ? files : localFiles;
  const currentPreviews = previews.length > 0 ? previews : localPreviews;
  const hasMultipleFiles = multiple && currentFiles.length > 0;

  return (
    <div className="receipt-upload-container">
      <label style={{ 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '500',
        color: darkMode ? 'rgba(255,255,255,0.9)' : '#333'
      }}>
        {label}
      </label>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,image/heic,image/heif"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept={`${accept},image/heic,image/heif,.heic,.heif`}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Upload area */}
      {!currentPreview && !file && !hasMultipleFiles && (
        <div
          className={`receipt-upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${dragActive ? '#60a5fa' : 'rgba(255,255,255,0.2)'}`,
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
            background: dragActive ? 'rgba(96, 165, 250, 0.1)' : 'rgba(255,255,255,0.03)',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Icon name="camera" size={32} color="gray" darkMode={darkMode} />
          </div>
          
          <p style={{ 
            color: 'rgba(255,255,255,0.5)', 
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {multiple 
              ? `Take photos or select from gallery (up to ${maxFiles} images)`
              : 'Take a photo or select from gallery'
            }
          </p>

          {/* Action buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              type="button"
              onClick={handleCameraClick}
              disabled={disabled}
              className="btn btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                minHeight: '48px',
                fontSize: '15px',
                fontWeight: '500'
              }}
            >
              <Icon name="camera" size={20} color="white" darkMode={true} />
              Camera
            </button>
            
            <button
              type="button"
              onClick={handleGalleryClick}
              disabled={disabled}
              className="btn btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                minHeight: '48px',
                fontSize: '15px',
                fontWeight: '500'
              }}
            >
              <Icon name="image" size={20} color="gray" darkMode={darkMode} />
              {multiple ? 'Select Images' : 'Gallery'}
            </button>
          </div>

          <p style={{ 
            color: 'rgba(255,255,255,0.3)', 
            fontSize: '12px',
            marginTop: '16px'
          }}>
            {multiple ? 'or drag & drop files here' : 'or drag & drop a file here'}
          </p>
          
          {multiple && (
            <p style={{ 
              color: 'rgba(96, 165, 250, 0.7)', 
              fontSize: '11px',
              marginTop: '8px'
            }}>
              For long receipts, you can upload multiple images
            </p>
          )}
        </div>
      )}

      {/* Multi-file preview area */}
      {hasMultipleFiles && (
        <div style={{
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '12px',
          padding: '16px',
          background: 'rgba(255,255,255,0.03)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <span style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {currentFiles.length} image{currentFiles.length > 1 ? 's' : ''} selected
            </span>
            {currentFiles.length < maxFiles && (
              <button
                type="button"
                onClick={handleGalleryClick}
                disabled={disabled}
                className="btn btn-secondary"
                style={{
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                + Add More
              </button>
            )}
          </div>
          
          {/* Grid of image previews */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '10px',
            marginBottom: '16px'
          }}>
            {currentFiles.map((f, index) => (
              <div 
                key={index}
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#000',
                  aspectRatio: '1',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                {currentPreviews[index] && (
                  <img 
                    src={currentPreviews[index]} 
                    alt={`Receipt ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  left: '4px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  #{index + 1}
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'rgba(239, 68, 68, 0.9)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '12px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(0,0,0,0.7)',
                  padding: '4px',
                  fontSize: '9px',
                  color: 'rgba(255,255,255,0.7)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {f.name}
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons for multi-file */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            flexWrap: 'wrap'
          }}>
            {showAIButton && (
              <button
                type="button"
                onClick={onAIExtract}
                disabled={disabled || extracting || currentFiles.length === 0}
                className="btn btn-primary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  minHeight: '48px',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
              >
                {extracting ? (
                  <>
                    <div className="spinner-small" />
                    <span>Reading {currentFiles.length} image{currentFiles.length > 1 ? 's' : ''} with AI... (this takes ~15s)</span>
                  </>
                ) : (
                  <>
                    <Icon name="details" size={20} color="white" darkMode={true} />
                    Extract All with AI
                  </>
                )}
              </button>
            )}
            
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="btn btn-danger"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                minHeight: '48px',
                fontSize: '14px'
              }}
            >
              <Icon name="delete" size={18} color="white" darkMode={true} />
              Clear All
            </button>
          </div>

          {/* Extracted data display for multi-file */}
          {extractedData && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(34, 197, 94, 0.2)'
              }}>
                <Icon name="verified" size={20} color="success" darkMode={darkMode} />
                <span style={{ 
                  fontWeight: '600', 
                  color: '#22c55e',
                  fontSize: '15px'
                }}>
                  AI Extracted Data (from {currentFiles.length} image{currentFiles.length > 1 ? 's' : ''})
                </span>
              </div>
              
              {/* Service items from all receipts */}
              {extractedData.service_items && extractedData.service_items.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: '500' }}>
                    Service Items ({extractedData.service_items.length})
                  </div>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.03)', 
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {extractedData.service_items.map((item, index) => (
                      <div 
                        key={index}
                        style={{ 
                          padding: '10px 12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: index < extractedData.service_items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', color: '#fff' }}>{item.description}</div>
                          {item.quantity > 1 && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                              Qty: {item.quantity} × {extractedData.currency || '£'}{item.unit_price?.toFixed(2) || '0.00'}
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#22c55e',
                          marginLeft: '12px'
                        }}>
                          {extractedData.currency || '£'}{item.total?.toFixed(2) || (item.unit_price * (item.quantity || 1))?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.15)',
                borderRadius: '8px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>Total</span>
                  <span style={{ color: '#22c55e', fontWeight: '700', fontSize: '18px' }}>
                    {extractedData.currency || '£'}{extractedData.total_cost?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Single file preview area */}
      {!hasMultipleFiles && (currentPreview || file) && (
        <div style={{
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '12px',
          padding: '16px',
          background: 'rgba(255,255,255,0.03)'
        }}>
          {/* Image preview */}
          {currentPreview && (
            <div style={{ 
              marginBottom: '16px',
              borderRadius: '8px',
              overflow: 'hidden',
              maxHeight: '300px',
              display: 'flex',
              justifyContent: 'center',
              background: '#000'
            }}>
              <img 
                src={currentPreview} 
                alt="Receipt preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px',
                  objectFit: 'contain'
                }} 
              />
            </div>
          )}

          {/* File info */}
          {file && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              background: 'rgba(96, 165, 250, 0.1)',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Icon name="details" size={20} color="primary" darkMode={darkMode} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ 
                  margin: 0, 
                  fontWeight: '500',
                  fontSize: '14px',
                  color: darkMode ? '#fff' : '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {file.name}
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.5)'
                }}>
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            flexWrap: 'wrap'
          }}>
            {/* AI Extract button - only for Professional+ tier */}
            {showAIButton && (
              <button
                type="button"
                onClick={onAIExtract}
                disabled={disabled || extracting || !file}
                className="btn btn-primary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  minHeight: '48px',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
              >
                {extracting ? (
                  <>
                    <div className="spinner-small" />
                    <span>Reading receipt with AI... (~15s)</span>
                  </>
                ) : (
                  <>
                    <Icon name="details" size={20} color="white" darkMode={true} />
                    Extract with AI
                  </>
                )}
              </button>
            )}

            {/* Change/Remove buttons */}
            <button
              type="button"
              onClick={handleGalleryClick}
              disabled={disabled}
              className="btn btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                minHeight: '48px',
                fontSize: '14px'
              }}
            >
              <Icon name="edit" size={18} color="gray" darkMode={darkMode} />
              Change
            </button>

            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="btn btn-danger"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                minHeight: '48px',
                fontSize: '14px'
              }}
            >
              <Icon name="delete" size={18} color="white" darkMode={true} />
              Remove
            </button>
          </div>

          {/* Extracted data display */}
          {extractedData && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(34, 197, 94, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="verified" size={20} color="success" darkMode={darkMode} />
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#22c55e',
                    fontSize: '15px'
                  }}>
                    AI Extracted Data
                  </span>
                </div>
                {extractedData.confidence && (
                  <span style={{
                    padding: '4px 8px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#22c55e'
                  }}>
                    {Math.round(extractedData.confidence * 100)}% confidence
                  </span>
                )}
              </div>
              
              {/* Main info grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                marginBottom: extractedData.service_items?.length > 0 ? '16px' : 0
              }}>
                {extractedData.frame_number && (
                  <div style={{ padding: '10px', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '8px', gridColumn: 'span 2' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Frame Number</div>
                    <div style={{ fontSize: '16px', color: '#60a5fa', fontWeight: '700', fontFamily: 'monospace' }}>{extractedData.frame_number}</div>
                  </div>
                )}
                {(extractedData.bike_make || extractedData.bike_model) && (
                  <div style={{ padding: '10px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Bike</div>
                    <div style={{ fontSize: '14px', color: '#a78bfa', fontWeight: '600' }}>
                      {[extractedData.bike_make, extractedData.bike_model].filter(Boolean).join(' ')}
                    </div>
                  </div>
                )}
                {extractedData.service_date && (
                  <div style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Date</div>
                    <div style={{ fontSize: '14px', color: '#fff' }}>{extractedData.service_date}</div>
                  </div>
                )}
                {extractedData.customer_name && (
                  <div style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Customer</div>
                    <div style={{ fontSize: '14px', color: '#fff' }}>{extractedData.customer_name}</div>
                  </div>
                )}
                {extractedData.receipt_id && (
                  <div style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Receipt #</div>
                    <div style={{ fontSize: '14px', color: '#fff' }}>{extractedData.receipt_id}</div>
                  </div>
                )}
              </div>

              {/* Service items list */}
              {extractedData.service_items && extractedData.service_items.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: '500' }}>
                    Service Items ({extractedData.service_items.length})
                  </div>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.03)', 
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {extractedData.service_items.map((item, index) => (
                      <div 
                        key={index}
                        style={{ 
                          padding: '10px 12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: index < extractedData.service_items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', color: '#fff' }}>{item.description}</div>
                          {item.quantity > 1 && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                              Qty: {item.quantity} × {extractedData.currency || '£'}{item.unit_price?.toFixed(2) || '0.00'}
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#22c55e',
                          marginLeft: '12px'
                        }}>
                          {extractedData.currency || '£'}{item.total?.toFixed(2) || (item.unit_price * (item.quantity || 1))?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description if no items */}
              {extractedData.description && (!extractedData.service_items || extractedData.service_items.length === 0) && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Description</div>
                  <div style={{ fontSize: '14px', color: '#fff' }}>{extractedData.description}</div>
                </div>
              )}

              {/* Totals */}
              <div style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.15)',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                {extractedData.subtotal && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Subtotal</span>
                    <span style={{ color: '#fff' }}>{extractedData.currency || '£'}{extractedData.subtotal?.toFixed(2)}</span>
                  </div>
                )}
                {extractedData.tax && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Tax</span>
                    <span style={{ color: '#fff' }}>{extractedData.currency || '£'}{extractedData.tax?.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  paddingTop: extractedData.subtotal || extractedData.tax ? '8px' : 0,
                  borderTop: extractedData.subtotal || extractedData.tax ? '1px solid rgba(255,255,255,0.1)' : 'none'
                }}>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>Total</span>
                  <span style={{ color: '#22c55e', fontWeight: '700', fontSize: '18px' }}>
                    {extractedData.currency || '£'}{extractedData.total_cost?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>

              <p style={{ 
                fontSize: '12px', 
                color: 'rgba(255,255,255,0.4)', 
                marginTop: '12px',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                Review the extracted data above. Fields have been auto-filled in the form.
              </p>
            </div>
          )}
        </div>
      )}

      <style>{`
        .receipt-upload-container {
          width: 100%;
        }
        
        .receipt-upload-area:hover {
          border-color: rgba(96, 165, 250, 0.5) !important;
          background: rgba(96, 165, 250, 0.05) !important;
        }
        
        .spinner-small {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 480px) {
          .receipt-upload-area {
            padding: 20px 16px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ReceiptUpload;
